/**
 * CMS Podcast Queries
 *
 * Server-side queries for fetching podcast data.
 */

import { query } from "@solidjs/router";
import type { CMSPodcast, CMSPodcastResponse } from "~/types/cms";
import { env } from "~/config/env";
import { isPreviewTokenValid } from "~/utils/previewToken.server";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS, findByTenant, emptyPaginatedResponse } from "../queryUtils";
import { buildPodcastsListUrl, buildPodcastBySlugUrl } from "../queryUrls";

/**
 * Server-side query for fetching all podcasts with pagination
 *
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param page - Page number (default: 1)
 * @param limit - Number of podcasts per page (default: 12)
 * @param station - Optional station filter
 * @returns Paginated podcasts list
 */
export const getAllPodcastsQuery = query(
  async (tenantSlug: string, page: number = 1, limit: number = 12, station?: string) => {
    "use server";

    const LOG_PREFIX = "[allPodcasts]";

    // URL built via shared queryUrls module (also used by performance audit)
    const url = `${env.cmsApiUrl}${buildPodcastsListUrl({ page, limit, station, tenantSlug })}`;

    const data = await fetchFromCMS<CMSPodcastResponse>(url, { logPrefix: LOG_PREFIX });

    if (!data?.docs || !Array.isArray(data.docs)) {
      logger.warn(`${LOG_PREFIX} Invalid response structure`);
      return emptyPaginatedResponse<CMSPodcast>(page, limit);
    }

    logger.info(`${LOG_PREFIX} Found ${data.docs.length} podcasts (total: ${data.totalDocs || 0})`);
    return data;
  },
  "allPodcasts"
);

/**
 * Server-side query for fetching a podcast by slug
 *
 * @param slug - The podcast slug (e.g., 'my-podcast')
 * @param tenantSlug - The tenant slug for multi-tenancy filtering
 * @param previewToken - CMS-signed preview token; drafts are read only when it verifies server-side for the tenant
 * @returns Podcast data or null if not found
 */
export const getPodcastBySlugQuery = query(
  async (slug: string, tenantSlug: string, previewToken?: string) => {
    "use server";
    const draft = isPreviewTokenValid(previewToken, tenantSlug);

    const LOG_PREFIX = "[podcastBySlug]";

    // URL built via shared queryUrls module (also used by performance audit)
    const url = `${env.cmsApiUrl}${buildPodcastBySlugUrl({ slug, tenantSlug, draft })}`;

    const data = await fetchFromCMS<CMSPodcastResponse>(url, {
      logPrefix: LOG_PREFIX,
      previewToken: draft ? previewToken : undefined,
    });

    if (!data?.docs || data.docs.length === 0) {
      logger.warn(`${LOG_PREFIX} No podcast found with slug: ${slug}`);
      return null;
    }

    // Payload ignores nested tenant[slug] filters — filter client-side
    const podcast = findByTenant(data.docs, tenantSlug);

    if (!podcast) {
      logger.warn(`${LOG_PREFIX} No podcast found for tenant: ${tenantSlug}`);
      return null;
    }

    logger.info(`${LOG_PREFIX} Found podcast: ${podcast.title}`);
    return podcast as CMSPodcast;
  },
  "podcastBySlug"
);
